import {
  DataValidationReport, DataValidationTask,
  ValidationDataModel,
  ValidationDataType,
  ValidationResultModel, ValidationStatus,
} from "../services/models/validation-model";
import {SCHEMA_HYDRA_MEMBER, SCHEMA_TYPE} from "./soml-utils";
import moment from 'moment';

const TYPE = '@type';

const VALIDATION_DATES_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export function resolveErrorModel(response: any): ValidationDataModel[] {
  if (response[SCHEMA_TYPE] === 'hydra:Collection' || response[SCHEMA_HYDRA_MEMBER]) {
    return jsonLdResponseToModel(response[SCHEMA_HYDRA_MEMBER]);
  } else if (response[SCHEMA_TYPE] === 'Error') {
    return jsonLdResponseToModel([response]);
  } else if (response.name === 'YAMLException' || response.code === 'YAMLException') {
    return yamlValidationToModel(response);
  }
  return jsonResponseToModel(response);
}

/**
 * Data and schema validation response transformer
 */
function jsonLdResponseToModel(response: Array<any>): ValidationDataModel[] {
  return response.map((item: any) => {
    return {
      type: resolveErrorLevel(item[TYPE]),
      message: item.message
    };
  });
}

/**
 * YAML validation errors
 */
function yamlValidationToModel(yamlError: any): ValidationDataModel[] {
  return [{
    type: ValidationDataType.ERROR,
    message: yamlError.message
  }];
}

/**
 * Schema validation response transformer.
 */
function jsonResponseToModel(response: Array<any>): ValidationDataModel[] {
  return response.map((item: any) => {
    return {
      type: ValidationDataType.ERROR,
      message: item.message
    };
  });
}

function resolveErrorLevel(type: string): ValidationDataType {
  switch (type) {
    case 'Error': return ValidationDataType.ERROR;
    case 'Warning': return ValidationDataType.WARNING;
    case 'Information': return ValidationDataType.INFO;
    default: return ValidationDataType.UNKNOWN;
  }
}

type ValidationTask = {
  id: string;
  durationInMs: number;
  objectType: string;
  propertyName: string;
  status: string;
  validatorName: string;
  result: string[];
  queryable: boolean;
}

type ValidationStatusResponse = {
  id: string;
  schemaId: string;
  active: boolean;
  done: boolean;
  createdOn: string;
  completedOn: string;
  canceledOn: string;
  totalTasks: number,
  remainingTasks: number;
  infoTasks: number;
  warningTasks: number;
  failedTasks: number;
  tasks: Array<ValidationTask>;
}

function taskToValidationModel(tasks: Array<DataValidationTask> = [], type: ValidationDataType) {
  return tasks.map((task) => {
    return {
      message: task.result,
      type
    }
  });
}

export function convertToValidationResult(report: DataValidationReport): ValidationResultModel {
  return {
    info: taskToValidationModel(report.infoTasks, ValidationDataType.INFO),
    warnings: taskToValidationModel(report.warningTasks, ValidationDataType.WARNING),
    errors: taskToValidationModel(report.failedTasks, ValidationDataType.ERROR)
  };
}

export function transformDataValidationResponse(validationResponse: ValidationStatusResponse): DataValidationReport {
  let validationStatus;
  if (validationResponse.done) {
    if (validationResponse.failedTasks > 0) {
      validationStatus = ValidationStatus.FAILED;
    } else {
      validationStatus = ValidationStatus.DONE;
    }
  } else if (validationResponse.canceledOn) {
      validationStatus = ValidationStatus.CANCELED
  } else {
    validationStatus  = ValidationStatus.RUNNING;
  }

  return {
    id: validationResponse.id,
    schemaId: validationResponse.schemaId,
    done: validationResponse.done,
    status: validationStatus,
    createdOn: formatDate(validationResponse.createdOn),
    completedOn: formatDate(validationResponse.completedOn),
    canceledOn: formatDate(validationResponse.canceledOn),
    totalTasksCount: validationResponse.totalTasks,
    remainingTasksCount: validationResponse.remainingTasks,
    infoTasksCount: validationResponse.infoTasks,
    warningTasksCount: validationResponse.warningTasks,
    failedTasksCount: validationResponse.failedTasks,
    successTasks: filterTaskByStatus(validationResponse, 'SUCCESS'),
    infoTasks: filterTaskByStatus(validationResponse, 'INFO'),
    warningTasks: filterTaskByStatus(validationResponse, 'WARN'),
    failedTasks: filterTaskByStatus(validationResponse, 'FAIL')
  };
}

function filterTaskByStatus(validationResponse: ValidationStatusResponse, status: string): DataValidationTask[] {
  if (!validationResponse.tasks || !validationResponse.tasks.length) {
    return [];
  }
  return validationResponse.tasks
    .filter((task) => task.status === status)
    .map((task) => {
      return {
        id: task.id,
        status: task.status,
        objectType: task.objectType,
        propertyName: task.propertyName,
        durationInMs: task.durationInMs,
        result: task.result?.join('\n'),
        queryable: task.queryable
      }
    })
}

function formatDate(timestamp: string): string | undefined {
  return timestamp && moment(timestamp).format(VALIDATION_DATES_FORMAT);
}
